import { useEffect, useState } from 'react'
import Speaker from '../components/Speaker.jsx'
import { speak } from '../lib/speech.js'
import { track } from '../lib/analytics.js'

/**
 * SPEC screen 7: three questions, big buttons, cheer on correct, gentle
 * "try again" on wrong. Skippable at any point.
 *
 * Polish (step 9): after two wrong tries the right answer is shown and read
 * out, so a kid is never stuck on a question they cannot get past; the score
 * still only counts a first-try answer (D-060).
 */
const CHEERS = ['Yes! 🎉', 'You got it! ⭐', 'Right! 🦁', 'Nailed it! 🏆']

export default function Quiz({ story, age, onDone }) {
  const [i, setI] = useState(0)
  const [picked, setPicked] = useState(null)
  const [wrong, setWrong] = useState(new Set())
  const [score, setScore] = useState(0)
  const [firstTry, setFirstTry] = useState(true)
  const [revealed, setRevealed] = useState(false)

  const done = i >= story.quiz.length
  const q = story.quiz[i]

  useEffect(() => {
    setI(0); setPicked(null); setWrong(new Set()); setScore(0); setFirstTry(true); setRevealed(false)
  }, [story])

  // `done` flips exactly once per quiz, so this records the score once.
  useEffect(() => {
    if (done) track('quiz_done', { score, of: story.quiz.length })
  }, [done]) // eslint-disable-line react-hooks/exhaustive-deps

  const nextQuestion = () => {
    setI((n) => n + 1)
    setPicked(null); setWrong(new Set()); setFirstTry(true); setRevealed(false)
  }

  const choose = (n) => {
    if (picked !== null) return
    track('quiz_answer', { i, correct: n === q.answer, first_try: firstTry })
    if (n === q.answer) {
      setPicked(n)
      if (firstTry) setScore((s) => s + 1)
      speak(CHEERS[i % CHEERS.length].replace(/[^\w! ]/g, ''))
      setTimeout(nextQuestion, 1200)
      return
    }
    const nextWrong = new Set(wrong).add(n)
    setWrong(nextWrong)
    setFirstTry(false)
    if (nextWrong.size >= 2 || nextWrong.size >= q.options.length - 1) {
      setRevealed(true)
      speak(`The answer is ${q.options[q.answer]}.`)
    } else {
      speak('Not that one. Try again!')
    }
  }

  if (done) {
    const perfect = score === story.quiz.length
    const line = perfect ? 'Perfect! You know your animals!' : score > 0 ? 'Nice work!' : 'Good try! Every battle teaches something.'
    return (
      <section className="screen quiz">
        <div className={`quiz__score${perfect ? ' is-perfect' : ''}`}>{score} / {story.quiz.length}</div>
        <h1 className="quiz__q">{line}</h1>
        <Speaker text={`You got ${score} out of ${story.quiz.length}. ${line}`} auto={age !== '6'} size="lg" />
        <button className="btn btn--fight btn--huge" onClick={onDone}>Battle again!</button>
      </section>
    )
  }

  const spoken = `${q.q} ${q.options.map((o, n) => `${n + 1}: ${o}.`).join(' ')}`
  const feedback = picked !== null ? CHEERS[i % CHEERS.length] : revealed ? `The answer is ${q.options[q.answer]}` : wrong.size ? 'Not that one. Try again!' : ''

  return (
    <section className="screen quiz">
      <div className="quiz__progress" aria-label={`Question ${i + 1} of ${story.quiz.length}`}>
        {story.quiz.map((_, n) => <span key={n} className={`quiz__pip${n < i ? ' is-done' : n === i ? ' is-on' : ''}`} />)}
      </div>
      <h1 className="quiz__q">{q.q} <Speaker text={spoken} auto={age !== '6'} /></h1>
      <div className="quiz__options">
        {q.options.map((o, n) => (
          <button
            key={n}
            className={`btn quiz__opt${wrong.has(n) ? ' is-wrong' : ''}${picked === n || (revealed && n === q.answer) ? ' is-right' : ''}`}
            onClick={() => choose(n)}
            disabled={wrong.has(n) || picked !== null}
          >
            {o}
          </button>
        ))}
      </div>
      <div className={`quiz__feedback ${picked !== null ? 'quiz__feedback--yes' : 'quiz__feedback--no'}`} role="status">{feedback}</div>
      {revealed && picked === null && <button className="btn btn--primary" onClick={nextQuestion}>Next question ▶</button>}
      <button className="btn quiz__skip" onClick={() => { track('quiz_skipped', { at: i }); onDone() }}>Skip the quiz</button>
    </section>
  )
}
